import { createContext, useContext, useEffect, useMemo } from "react";
import { createStore, useStore } from "zustand";
import { persist } from "zustand/middleware";

import { useUserDetails } from "~/api/queries/useUserDetails";
import { DEFAULT_CURRENT_USER, DEFAULT_USER_DETAILS } from "~/constants/user";
import { useSubscriptionActivation } from "~/context/SubscriptionActivationContext";
import { useSession, type BetterAuthSessionUser } from "~/lib/better-auth-client";
import { useAuthStore } from "~/modules/Auth/authStore";
import { CURRENT_USER_STORAGE_KEY } from "~/types/shared";

import type { BetterAuthUser } from "@repo/shared";
import type { PropsWithChildren } from "react";
import type { StoreApi } from "zustand";
import type { GetUserDetailsResponse } from "~/api/generated-api";

interface CurrentUserStore {
  currentUser: BetterAuthSessionUser;
  setSessionUser: (user: this["currentUser"]) => void;
  userDetails: GetUserDetailsResponse["data"];
  setUserDetails: (details: this["userDetails"]) => void;
}

const createCurrentUserStore = () =>
  createStore<CurrentUserStore>()(
    persist(
      (set) => ({
        currentUser: DEFAULT_CURRENT_USER,
        setSessionUser: (currentUser) => set({ currentUser }),
        userDetails: DEFAULT_USER_DETAILS,
        setUserDetails: (userDetails) => set({ userDetails }),
      }),
      {
        name: CURRENT_USER_STORAGE_KEY,
        partialize: (state) => ({
          currentUser: state.currentUser,
          userDetails: state.userDetails,
        }),
      },
    ),
  );

const CurrentUserStoreContext = createContext<StoreApi<CurrentUserStore> | null>(null);

const SessionManager = ({ store }: { store: StoreApi<CurrentUserStore> }) => {
  const { data: session, isPending } = useSession();
  const setLoggedIn = useAuthStore((state) => state.setLoggedIn);
  const { executeActivationIfAvailable } = useSubscriptionActivation();

  const currentUser = useMemo(
    () => (session?.user as BetterAuthUser) || DEFAULT_CURRENT_USER,
    [session?.user],
  );
  const userId = useMemo(() => currentUser?.id, [currentUser?.id]);
  const isUserBanned = useMemo(() => !!currentUser?.bannedAt, [currentUser?.bannedAt]);

  const { data: userDetails, isLoading: isUserDetailsLoading } = useUserDetails(userId, {
    enabled: !isPending && !!userId && !isUserBanned,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (!isPending) {
      const isLoggedIn = !!currentUser?.id;
      store.getState().setSessionUser(currentUser as BetterAuthUser);
      setLoggedIn(isLoggedIn);

      if (currentUser?.id) {
        executeActivationIfAvailable(currentUser.id, currentUser.role);
      }
    }
  }, [currentUser, isPending, store, setLoggedIn, session, executeActivationIfAvailable]);

  useEffect(() => {
    if (!isUserDetailsLoading && userDetails) {
      store.getState().setUserDetails(userDetails);
    }
  }, [userDetails, isUserDetailsLoading, store]);

  return null;
};

export const CurrentUserStoreProvider = ({ children }: PropsWithChildren) => {
  const store = useMemo(() => createCurrentUserStore(), []);

  return (
    <CurrentUserStoreContext.Provider value={store}>
      <SessionManager store={store} />
      {children}
    </CurrentUserStoreContext.Provider>
  );
};

export const useCurrentUserStore = () => {
  const store = useContext(CurrentUserStoreContext);

  if (!store) {
    throw new Error("useCurrentUserStore must be used within a CurrentUserStoreProvider");
  }

  return useStore(store);
};

export const useCurrentUser = () => {
  const { currentUser } = useCurrentUserStore();
  return currentUser;
};

export const useCurrentUserDetails = () => {
  const { userDetails } = useCurrentUserStore();
  return userDetails;
};

export const useIsLoggedIn = () => {
  const currentUser = useCurrentUser();
  return !!currentUser;
};
